/*

  ______  _____   ______ _______  ______
 |_____/ |     | |  ____ |______ |_____/
 |    \_ |_____| |_____| |______ |    \_

 vehicle sales system

(c) 2006 - 2019 TCE <TCE-ITDevelopers@toyota-ce.com>

Licensed under the Apache License, Version 2.0 (the "License");
you may not use this file except in compliance with the License.
You may obtain a copy of the License at

http://www.apache.org/licenses/LICENSE-2.0

Unless required by applicable law or agreed to in writing, software
distributed under the License is distributed on an "AS IS" BASIS,
WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
See the License for the specific language governing permissions and
limitations under the License.

*/

'use strict';

const angular = require("angular");
const d3 = require("d3");

angular
    .module('ROGER')
    .factory('graphService', GraphService);

GraphService.$inject = ['$http'];

function GraphService($http) {

    var data = null;

    var service = {
        setData: setData,
        renderGraph: renderGraph,
        removeGraph: removeGraph
    };

    function setData(dataIn) {
        data = dataIn;
    }

    function removeGraph(appendElement) {
        d3.select(appendElement).selectAll("*").remove();
    }

    function renderGraph(chartWidth, appendElement) {
        chartWidth = chartWidth || 790;
        appendElement = appendElement || 'body';

        if (data === null) {
            console.error('Data are missing.');
        } else {
            var margin = {
                top: 5,
                right: 40,
                bottom: 20,
                left: 120
            };
            var width = chartWidth - margin.left - margin.right;
            var height = 50 - margin.top - margin.bottom;

            createGraph();
            var chart = d3.bullet()
                .width(width)
                .height(height);

            var svg = d3.select(appendElement).selectAll("svg")
                .data(data)
                .enter().append("svg")
                .attr("class", "bullet")
                .attr("width", width + margin.left + margin.right)
                .attr("height", height + margin.top + margin.bottom)
                .append("g")
                .attr("transform", "translate(" + margin.left + "," + margin.top + ")")
                .call(chart);

            var title = svg.append("g")
                .style("text-anchor", "end")
                .attr("transform", "translate(-6," + height / 2 + ")");

            title.append("text")
                .attr("class", "title")
                .text(function (d) {
                    return d.title;
                });

            title.append("text")
                .attr("class", "subtitle")
                .attr("dy", "1em")
                .text(function (d) {
                    return d.subtitle;
                });
        }
    }

    function createGraph() {
        d3.bullet = function () {
            var reverse = false,
                duration = 0,
                width = 380,
                height = 30;

            // For each small multiple…
            function bullet(g) {
                g.each(function (d, i) {
                    var g = d3.select(this);

                    var target = bulletTarget(d);
                    var actual = bulletActual(d);
                    var percent = bulletPercent(d);
                    var tickFormat = bulletFormat(d);

                    // Compute the new x-scale.
                    var x1 = d3.scaleLinear()
                        .domain([0, Math.max(target, actual)])
                        .range(reverse ? [width, 0] : [0, width]);

                    // Retrieve the old x-scale, if this is an update.
                    var x0 = this.__chart__ || d3.scaleLinear()
                        .domain([0, Infinity])
                        .range(x1.range());

                    // Stash the new scale.
                    this.__chart__ = x1;

                    // Derive width-scales from the x-scales.
                    var w0 = bulletWidth(x0),
                        w1 = bulletWidth(x1);

                    // Add target
                    var range = g.selectAll("rect.range")
                        .data([target]);

                    range.enter().append("rect")
                        .attr("class", function (d, i) { return "range s" + i; })
                        .attr("width", w0)
                        .attr("height", height)
                        .attr("x", reverse ? x0 : 0)
                        .transition()
                        .duration(duration)
                        .attr("width", w1)
                        .attr("x", reverse ? x1 : 0);

                    range.transition()
                        .duration(duration)
                        .attr("x", reverse ? x1 : 0)
                        .attr("width", w1)
                        .attr("height", height);

                    // Add actual
                    var measure = g.selectAll("rect.measure")
                        .data([actual]);

                    measure.enter().append("rect")
                        .attr("class", function (d, i) {
                            if (actual >= target) {
                                return "measure s" + i + " fulfilled";
                            }
                            return "measure s" + i;
                        })
                        .attr("width", w0)
                        .attr("height", height / 1)
                        .attr("x", reverse ? x0 : 0)
                        .attr("y", 0)
                        .transition()
                        .duration(duration)
                        .attr("width", w1)
                        .attr("x", reverse ? x1 : 0);

                    measure.transition()
                        .duration(duration)
                        .attr("width", w1)
                        .attr("height", height / 1)
                        .attr("x", reverse ? x1 : 0)
                        .attr("y", 0);

                    // Add target limits
                    range.enter().append("line")
                        .attr("class", "line")
                        .attr("x1", x0)
                        .attr("x2", x0)
                        .attr("y1", height / 6)
                        .attr("y2", height * 5 / 6)
                        .transition()
                        .duration(duration)
                        .attr("x1", x1)
                        .attr("x2", x1);

                    range.transition()
                        .duration(duration)
                        .attr("x1", x1)
                        .attr("x2", x1)
                        .attr("y1", 0)
                        .attr("y2", height);

                    // Add texts
                    var marker = g.selectAll("line.marker")
                        .data([actual]);

                    marker.enter().append("text")
                        .html(function () {
                            if (tickFormat !== null) {
                                return (actual * 100).toFixed(0) + "%";
                            }
                            return actual;
                        })
                        .attr("class", (actual >= target) ? "marker-actual achieved" : "marker-actual")
                        .attr("x", x1(actual) + 5)
                        .attr("y", 16);

                    marker.transition()
                        .duration(duration)
                        .attr("x", x1(actual) + 5)
                        .attr("y", 16);

                    if (percent >= 10 || (tickFormat !== null && percent >= 0.1)) {
                        marker.enter().append("text")
                            .html(function () {
                                if (tickFormat !== null) {
                                    return (percent * 100).toFixed(1) + "%";
                                }
                                return percent + "%";
                            })
                            .attr("class", "marker-percent")
                            .attr("x", x1(actual) / 2 - 20)
                            .attr("y", 16);

                        marker.transition()
                            .duration(duration)
                            .attr("x", x1(actual) / 2 - 20)
                            .attr("y", 16);
                    }

                    // Compute the tick format.
                    var format = (tickFormat !== null) ? d3.format(tickFormat) : x1.tickFormat(8);

                    // Update the tick groups.
                    var tick = g.selectAll("g.tick")
                        .data(x1.ticks(8), function (d) {
                            return this.textContent || format(d);
                        });

                    // Initialize the ticks with the old scale, x0.
                    var tickEnter = tick.enter().append("g")
                        .attr("class", "tick")
                        .attr("transform", bulletTranslate(x0))
                        .style("opacity", 1e-6);

                    tickEnter.append("line")
                        .attr("y1", height)
                        .attr("y2", height * 7 / 6);

                    tickEnter.append("text")
                        .attr("text-anchor", "middle")
                        .attr("dy", "1em")
                        .attr("y", height * 7 / 6)
                        .text(format);

                    // Transition the entering ticks to the new scale, x1.
                    tickEnter.transition()
                        .duration(duration)
                        .attr("transform", bulletTranslate(x1))
                        .style("opacity", 1);

                    // Transition the updating ticks to the new scale, x1.
                    var tickUpdate = tick.transition()
                        .duration(duration)
                        .attr("transform", bulletTranslate(x1))
                        .style("opacity", 1);

                    tickUpdate.select("line")
                        .attr("y1", height)
                        .attr("y2", height * 7 / 6);

                    tickUpdate.select("text")
                        .attr("y", height * 7 / 6);

                    // Transition the exiting ticks to the new scale, x1.
                    tick.exit().transition()
                        .duration(duration)
                        .attr("transform", bulletTranslate(x1))
                        .style("opacity", 1e-6)
                        .remove();
                });
                d3.timerFlush();
            }

            bullet.width = function (x) {
                if (!arguments.length) return width;
                width = x;
                return bullet;
            };

            bullet.height = function (x) {
                if (!arguments.length) return height;
                height = x;
                return bullet;
            };

            bullet.tickFormat = function (x) {
                if (!arguments.length) return tickFormat;
                tickFormat = x;
                return bullet;
            };

            bullet.duration = function (x) {
                if (!arguments.length) return duration;
                duration = x;
                return bullet;
            };

            return bullet;
        };

        function bulletTarget(data) {
            return data.target;
        }

        function bulletPercent(data) {
            return data.percent;
        }

        function bulletActual(data) {
            return data.actual;
        }

        function bulletFormat(data) {
            return data.format;
        }

        function bulletTranslate(x) {
            return function (d) {
                return "translate(" + x(d) + ",0)";
            };
        }

        function bulletWidth(x) {
            var x0 = x(0);
            return function (d) {
                return Math.abs(x(d) - x0);
            };
        }
    }

    return service;
}
